import { notificationToastOptions, toaster } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { selectSensorRoleActionsFromActiveProfile } from '@redux/authent/authent.selectors';
import { selectAllOngoingNotifications } from '@redux/data/data.selectors';
import { deleteNotificationFromQueue } from '@redux/global/global.reducer';
import { selectNotificationQueue } from '@redux/global/global.selector';
import { useCallback, useEffect, useRef } from 'react';

import { ProcedureStateEnum } from '@/types/config/procedure.types';

export function ToastNotificationContainer() {
  const notificationQueue = useAppSelector(selectNotificationQueue);
  const notifications = useAppSelector(selectAllOngoingNotifications);
  const removeNotificationFromQueue = useWithDispatch(deleteNotificationFromQueue);
  const toastIds = useRef<string[]>([]);
  const userActions = useAppSelector(selectSensorRoleActionsFromActiveProfile);

  const handleToastClose = useCallback((id: string) => {
    toaster.remove(id);
  }, []);

  useEffect(() => {
    //If queue is not empty and max number of toast is not reached
    //create a toast with the next alert in queue and remove it from the queue
    if (notificationQueue.length > 0 && toastIds.current.length < 10) {
      const notification = notificationQueue[0];

      queueMicrotask(() => {
        toaster.create({
          id: notification.id.toString(),
          meta: {
            notification,
            onClose: () => {
              handleToastClose(notification.id);
              toastIds.current = toastIds.current.filter((id) => id !== notification.id);
            },
          },
          ...notificationToastOptions,
        });

        toastIds.current = toastIds.current.concat(notification.id);
        removeNotificationFromQueue(notification);
      });
    }
  }, [notificationQueue, removeNotificationFromQueue, userActions, handleToastClose]);

  const cleanUpToasts = useCallback(() => {
    toastIds.current = toastIds.current.filter((id) => {
      const notification = notifications.find((n) => n.id === id);
      if (notification?.procedureState === ProcedureStateEnum.TO_PROCESS) {
        return true;
      }
      handleToastClose(id);
      return false;
    });
  }, [handleToastClose, notifications]);

  useEffect(() => {
    const interval = setInterval(cleanUpToasts, 5000);

    return () => clearInterval(interval);
  }, [cleanUpToasts, notifications]);

  useEffect(() => {
    return () => {
      toastIds.current.forEach((id) => handleToastClose(id));
    };
  }, [handleToastClose]);

  return null;
}
